import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { useHistory } from 'react-router-dom';
import Container from 'components/v2/container';
import CargomaticCard from 'components/v2/card';
import Breadcrumbs from 'components/v2/Breadcrumbs';
import { Button } from 'react-bootstrap';
import TextArea from 'components/v2/forms/TextArea';
import Alert from 'components/ui/Alert';
import { showToast } from 'stores/ToastStore';
import {
  submitEmptyNotifyContainersRequest,
  getShipperWarehouseRelation,
} from 'utils/WarehouseUserAPIUtils';
import { sluggify } from 'utils/localization/utils';
import SessionStore from 'stores/SessionStore';
import LoadingCircle from 'components/ui/LoadingCircle';

const pageTestKey = 'warehouse-portal-empty-notify';

export default function EmptyNotify() {
  const { t } = useTranslation();
  const history = useHistory();
  const [containerStr, setContainerStr] = useState('');
  const [containerErrors, setContainerErrors] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const getShipperWarehouseRelationStatus = async () => {
    try {
      setIsLoading(true);
      const {
        entity: { data },
      } = await getShipperWarehouseRelation(true, true);
      const status = data?.length > 0;
      SessionStore.setShipperWarehouseRelationStatus(status);
    } catch (error) {
      SessionStore.setShipperWarehouseRelationStatus(false);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem('shipperWarehouseRelationStatus') === null) {
      getShipperWarehouseRelationStatus();
    }
  }, []);

  useEffect(() => {
    const invalidContainer = containerStr
      ?.split(/[\n,]+/)
      .map((item) => item.trim())
      .filter((item) => !item || item?.length < 10);
    setContainerErrors(invalidContainer?.join(', '));
  }, [containerStr]);

  const breadcrumbs = {
    pages: [
      {
        name: t('EMPTY_NOTIFY_PORTAL'),
        href: `/`,
        current: true,
      },
    ],
  };

  const handleSaveContainers = async () => {
    try {
      const containers = containerStr
        .split(/[\n,]+/)
        .map((item) => item.trim())
        .filter((item) => item);
      const response = await submitEmptyNotifyContainersRequest({ containers });
      if (response.entity?.success && response.entity?.result?.items) {
        const reportId = response.entity?.result?.items[0]?._id;
        showToast(t('EMPTY_NOTIFY'), t('EMPTY_NOTIFY_SAVE_SUCCESS'), 'success');
        if (reportId) {
          history.push(`/warehouse/empty-notify-requests/${reportId}`);
        } else {
          showToast('', t('REPORT_ID_NOT_FOUND'), 'error');
        }
      }
    } catch (error) {
      const errroMsg = error?.entity?.error || 'SOMETHING_WENT_WRONG';
      showToast(
        t('EMPTY_NOTIFY'),
        t('EMPTY_NOTIFY_SAVE_ERROR', { error: t(sluggify(errroMsg)) }),
        'error',
      );
    }
  };
  const status = SessionStore.getShipperWarehouseRelationStatus();
  const isShipperRelationEnabled = status === 'true';

  if (isLoading) {
    return (
      <Container className="lg:mt-0 mt-4">
        <Breadcrumbs className="mt-4" breadcrumbs={breadcrumbs} />
        <LoadingCircle text={t('LOADING')} className="mt-4" />
      </Container>
    );
  }

  return (
    <Container className="lg:mt-0 mt-4">
      <Breadcrumbs className="mt-4" breadcrumbs={breadcrumbs} />
      {!isShipperRelationEnabled && (
        <Alert type="warning">{t('USER_WAREHOUSE_MAPPING_WARNING')}</Alert>
      )}
      <div
        data-tesid={`${pageTestKey}-page`}
        className="lg:grid lg:grid-cols-1 lg:gap-6 mt-4"
      >
        <CargomaticCard
          header={() => (
            <div className="flex items-center justify-between">
              <h3 className="my-0">{t('EMPTY_NOTIFY')}</h3>
            </div>
          )}
          footer={() => (
            <div
              data-testid={`${pageTestKey}-footer`}
              className="form-actions gap-2"
            >
              <Button
                bsClass={`btn btn-default ${
                  !isShipperRelationEnabled ? 'cursor-not-allowed' : ''
                }`}
                onClick={() => setContainerStr('')}
                data-testid={`${pageTestKey}-cancel-button`}
                disabled={!isShipperRelationEnabled}
              >
                {titleize(t('CLEAR'))}
              </Button>
              <Button
                bsStyle="primary"
                data-testid={`${pageTestKey}-save-button`}
                onClick={() => handleSaveContainers()}
                disabled={!containerStr?.length || containerErrors?.length}
              >
                {titleize(t('SAVE'))}
              </Button>
            </div>
          )}
          showFooter
          grayFooter
        >
          <div className="text-sm font-bold px-0.5">
            {t('CONTAINER_NUMBERS')}
          </div>
          <div className="text-sm font-sans text-gray-500 p-0.5">
            {t('CONTAINER_NUMBERS_NOTES')}
          </div>
          <TextArea
            rows={8}
            value={containerStr}
            placeholder={t('COMMA_OR_LINE_SEPARATED_LIST')}
            onChange={(value) => setContainerStr(value)}
            disabled={!isShipperRelationEnabled}
          />
          {containerErrors && (
            <div className="mt-4">
              <Alert type="error">
                {t('VALIDATE_CONTAINERS_ID')}: {containerErrors}
              </Alert>
            </div>
          )}
        </CargomaticCard>
      </div>
    </Container>
  );
}
